/**
 * Les règles pures du fil de la séance du tuteur : quels signaux vivent, quand ils naissent, quand ils
 * s'escaladent, quand ils tombent. Port littéral de l'ancien panneau d'événements
 * (`common/liveMode/liveV3/tutorEventsPanel/eventsCatalog.js` et `useTutorEventsQueue.js`), de la garde
 * de la popup de lancement du champion (`common/hooks/checkpoint/TutorCheckpointManager.js`), et des
 * durées de vie tranchées le 02/11 (tech-derisk, point 12 ; registre L-01 à L-09).
 *
 * Les identifiants y sont des chaînes : les tranches Redux qu'écrit `LKEvents` portent des nombres,
 * l'écran les lit en chaînes (`String(id)`), et les clés persistées gardent la forme du legacy
 * (`${type}-${studentId}`), identique pour un id numérique.
 */
import {
    CHECKPOINT_CONTEXT_ATTRIBUTE,
    PREREQUISITE_QUIZ_CONTEXT,
} from "../../../../../engine/live/room/checkpointContext"

export type TutorLiveSignalType =
    "hand_raised" | "out_of_app" | "silence" | "exercise_done" | "sam_ongoing" | "auto_activity"

/** 10 min sans échange : le silence (`SILENCE_THRESHOLD_SECONDS`). */
export const SILENCE_THRESHOLD_SECONDS = 10 * 60
/** Hors de l'appli depuis 3 s au moins : l'anti-rebond (`OUT_OF_APP_DEBOUNCE_MS`) — l'état de la tuile, les hachures du fil. */
export const OUT_OF_APP_DEBOUNCE_MS = 3 * 1000
/** Sorti de l'appli depuis l'anti-rebond, hors caméra d'activité (photographier sa copie n'est pas sortir). */
export function isOutOfApp(outSince: number | undefined, now: number, camera: boolean): boolean {
    return !camera && outSince != null && now - outSince >= OUT_OF_APP_DEBOUNCE_MS
}
/**
 * Hors de l'appli depuis 3 min : l'alerte de « À traiter ». Une sortie plus courte (vérifier un message, une
 * calculatrice) ne laisse que ses hachures sur le fil, sans rien demander au masteur.
 */
export const OUT_OF_APP_ALERT_MS = 3 * 60 * 1000
/** Un début de signal persisté plus vieux ne peut venir que d'un autre live (`HYDRATION_MAX_AGE_MS`). */
export const HYDRATION_MAX_AGE_MS = 6 * 60 * 60 * 1000
/** Quatre minutes sans réponse : « Sans réponse — à toi de jouer » (`ESCALATION_DELAY_MS`). */
export const ESCALATION_DELAY_MS = 4 * 60 * 1000
/** L'activité choisie seul vit 10 min (Wajih, 02/11, point 12) ; la prod la gardait jusqu'au geste. */
export const AUTO_ACTIVITY_LIFETIME_MS = 10 * 60 * 1000

/*
 * L'élève dit de quoi parle sa conversation en cours (`ongoing_checkpoint`) par `checkpoint_context`
 * (`engine/live/room/checkpointContext`) : le quiz s'enregistre comme une conversation du champion, le tuteur ne le
 * voit jamais comme telle. Un élève sur une version d'avant ne le pose pas : son quiz se lit encore comme le champion,
 * jusqu'à sa mise à jour.
 */

/** Les élèves de la salle qui font leur quiz de prérequis en ce moment (leurs attributs LiveKit). */
export function quizParticipantIds(
    participants: readonly { id: string; isLocal?: boolean; attributes?: Record<string, string> }[]
): string[] {
    return participants
        .filter(
            (participant) =>
                participant.isLocal !== true &&
                participant.attributes?.ongoing_checkpoint === "1" &&
                participant.attributes?.[CHECKPOINT_CONTEXT_ATTRIBUTE] === PREREQUISITE_QUIZ_CONTEXT
        )
        .map((participant) => participant.id)
}

/** Les signaux que le champion peut prendre avant le tuteur (`SAM_ELIGIBLE`). */
export const SAM_ELIGIBLE: ReadonlySet<TutorLiveSignalType> = new Set(["hand_raised", "silence"])

/** L'auto-évaluation de l'élève, dite au tuteur (`AUTO_EVAL_LABELS`). */
export const AUTO_EVAL_LABELS: Record<string, string> = {
    got_it: "tout compris",
    mixed: "pas tout compris",
    lost: "bloqué",
}

/** Ce que dit la carte d'une activité choisie seul (`autoActivityVerb`). */
export function autoActivityVerb(evaluation: string | null | undefined): string {
    if (evaluation === "replay") return "a choisi de refaire la même activité"
    const label = evaluation != null ? AUTO_EVAL_LABELS[evaluation] : undefined
    return label != null
        ? `s'est auto-évalué « ${label} » et a choisi sa prochaine activité`
        : "a choisi seul sa prochaine activité"
}

/** Le verbe de chaque signal (`EVENT_CATALOG[type].verb`). */
export const SIGNAL_VERB: Record<TutorLiveSignalType, string> = {
    hand_raised: "lève la main",
    out_of_app: "est sorti de l'appli",
    silence: "n'a pas parlé depuis 10 min",
    exercise_done: "a soumis son exo",
    sam_ongoing: "discute avec",
    auto_activity: "a choisi sa prochaine activité",
}

/** Ce que les tranches de `liveMode` disent, lu en chaînes. */
export type TutorLiveSignalSources = {
    /** Les élèves inscrits (`liveSession.users` sans les tuteurs). */
    studentIds: string[]
    /** Les participants présents dans la salle (`liveParticipants`). */
    present: string[]
    raisedHands: string[]
    /** Le premier instant où l'écran a vu chaque élève hors de l'appli (l'anti-rebond). */
    outOfAppSince: Record<string, number>
    timesSinceLastCall: Record<string, number | undefined>
    soundModeIds: string[]
    ongoingCheckpointIds: string[]
    recentFinalizedActivities: Record<
        string,
        { activityId?: string | number; finishedAt?: string | number } | undefined
    >
    autoModeStartedActivities: Record<string, { activityId?: string | number; evaluation?: string } | undefined>
    now: number
}

export type TutorLiveSignal = {
    type: TutorLiveSignalType
    studentId: string
    /** La clé de l'horloge et de l'identité (`keyForEvent`). */
    key: string
    activityId?: string
    evaluation?: string
    finalizedAt?: string | number
}

/**
 * La clé d'un signal (`keyForEvent`) : un rendu et une activité choisie seul se distinguent par leur
 * activité (un 2ᵉ rendu est une nouvelle carte) ; les autres par l'élève.
 */
export function signalKey(signal: Pick<TutorLiveSignal, "type" | "studentId" | "activityId" | "finalizedAt">): string {
    if (signal.type === "exercise_done") {
        return `${signal.type}-${signal.studentId}-${signal.activityId ?? signal.finalizedAt ?? ""}`
    }
    if (signal.type === "auto_activity") return `${signal.type}-${signal.studentId}-${signal.activityId ?? ""}`
    return `${signal.type}-${signal.studentId}`
}

/** L'empreinte d'une carte (`eventHash`) : celle que la croix retient (`dismissedEventHashes`). */
export function signalHash(type: TutorLiveSignalType, studentId: string, startedAt: number): string {
    return `${type}-${studentId}-${startedAt}`
}

/** La clé d'un « Envoyer » le champion (`samSentAtByKey`) : le signal et l'élève, sans l'activité. */
export function samSentKey(type: TutorLiveSignalType, studentId: string): string {
    return `${type}-${studentId}`
}

/**
 * Les signaux vivants (le `useMemo` des événements de `useTutorEventsQueue`) : des élèves inscrits et
 * présents ; en Dialogue, seuls le rendu, l'activité choisie seul (le tuteur lui parle déjà) et la sortie de
 * l'appli.
 *
 * Écart voulu (daily du 04/11) : l'ancien taisait aussi la sortie de l'appli en Dialogue, si bien que remettre
 * un élève en Dialogue éteignait sa sortie comme un retour dans l'appli. Le mode du son ne dit rien de l'écran
 * de l'élève : seul son retour éteint le signal.
 */
export function tutorLiveSignals(sources: TutorLiveSignalSources): TutorLiveSignal[] {
    const present = new Set(sources.present)
    const signals: TutorLiveSignal[] = []
    const push = (signal: Omit<TutorLiveSignal, "key">) => signals.push({ ...signal, key: signalKey(signal) })
    for (const studentId of sources.studentIds) {
        if (!present.has(studentId)) continue
        const inDialogue = sources.soundModeIds.includes(studentId)
        if (!inDialogue) {
            if (sources.raisedHands.includes(studentId)) push({ type: "hand_raised", studentId })
            if ((sources.timesSinceLastCall[studentId] ?? 0) >= SILENCE_THRESHOLD_SECONDS)
                push({ type: "silence", studentId })
            if (sources.ongoingCheckpointIds.includes(studentId)) push({ type: "sam_ongoing", studentId })
        }
        const outSince = sources.outOfAppSince[studentId]
        if (outSince != null && sources.now - outSince >= OUT_OF_APP_ALERT_MS) push({ type: "out_of_app", studentId })
        const finalized = sources.recentFinalizedActivities[studentId]
        if (finalized != null) {
            push({
                type: "exercise_done",
                studentId,
                activityId: finalized.activityId != null ? String(finalized.activityId) : undefined,
                finalizedAt: finalized.finishedAt,
            })
        }
        const auto = sources.autoModeStartedActivities[studentId]
        if (auto != null) {
            push({
                type: "auto_activity",
                studentId,
                activityId: auto.activityId != null ? String(auto.activityId) : undefined,
                evaluation: auto.evaluation,
            })
        }
    }
    return signals
}

/**
 * Les instants de naissance (`startedAtRef`) : repris de la mémoire persistée (sauf au-delà de 6 h), sinon
 * maintenant. Rend aussi ce qu'il faut écrire (`syncTutorEventsStartedAt`) : les clés nouvelles, et celles
 * dont le signal est tombé.
 */
export function signalStartTimes(
    signals: TutorLiveSignal[],
    persisted: Record<string, number> | null | undefined,
    now: number
): { startedAt: Record<string, number>; additions: Record<string, number>; removals: string[] } {
    const startedAt: Record<string, number> = {}
    const additions: Record<string, number> = {}
    for (const signal of signals) {
        const kept = persisted?.[signal.key]
        if (kept != null && now - kept <= HYDRATION_MAX_AGE_MS) startedAt[signal.key] = kept
        else {
            startedAt[signal.key] = now
            additions[signal.key] = now
        }
    }
    const live = new Set(signals.map((signal) => signal.key))
    const removals = Object.keys(persisted ?? {}).filter((key) => !live.has(key))
    return { startedAt, additions, removals }
}

/**
 * L'élève à qui le tuteur parle en ce moment : en Dialogue ET présent (`dialogueWithStudentId`). Un élève
 * parti resté dans `soundModeIds` ne rend pas le tuteur « occupé ».
 */
export function dialogueWithStudentId(soundModeIds: string[], studentIds: string[], present: string[]): string | null {
    const here = new Set(present)
    return soundModeIds.find((id) => studentIds.includes(id) && here.has(id)) ?? null
}

/**
 * Quatre minutes sans réponse : depuis l'envoi du champion s'il est parti, sinon depuis le signal ;
 * seulement pour la main et le silence.
 */
export function isEscalated(
    type: TutorLiveSignalType,
    startedAt: number,
    samSentAt: number | null | undefined,
    now: number
): boolean {
    return SAM_ELIGIBLE.has(type) && now - (samSentAt ?? startedAt) >= ESCALATION_DELAY_MS
}

/** Le temps écoulé d'une carte (`formatElapsed`) : « 42s », puis « 3 min ». */
export function formatElapsed(startedAt: number, now: number): string {
    const seconds = Math.max(0, Math.floor((now - startedAt) / 1000))
    if (seconds < 60) return `${seconds}s`
    return `${Math.floor(seconds / 60)} min`
}

// ── La durée de vie et la validité ───────────────────────────────────────────────────────────────

/**
 * Les conditions de validité d'une suggestion, une liste fermée (tech-derisk, point 9, simplifié) : celles
 * qu'une suggestion de la V1 lit vraiment. Demain, le champion enverra des suggestions avec les mêmes
 * conditions, jugées par la même fonction.
 */
export type TutorSuggestionCondition =
    { kind: "hand_raised"; studentId: string } | { kind: "activity_open"; studentId: string; activityId: string }

/** L'état de la salle que la validité lit. */
export type TutorSuggestionRoom = {
    raisedHands: string[]
    /** L'activité que chaque élève a devant lui (`currentStudentActivity`). */
    openActivities: Record<string, string | undefined>
}

export type TutorValidity = { valid: true } | { valid: false; reason: "hand_lowered" | "activity_closed" }

/** La validité d'une suggestion contre la salle (`suggestionValidity`, point 9). */
export function suggestionValidity(condition: TutorSuggestionCondition, room: TutorSuggestionRoom): TutorValidity {
    switch (condition.kind) {
        case "hand_raised":
            return room.raisedHands.includes(condition.studentId)
                ? { valid: true }
                : { valid: false, reason: "hand_lowered" }
        case "activity_open":
            return room.openActivities[condition.studentId] === condition.activityId
                ? { valid: true }
                : { valid: false, reason: "activity_closed" }
    }
}

/** Ce qui finit une carte dont le signal vit encore : une durée, ou la fin de l'attente d'une validation. */
export type TutorSignalEnd = "expired" | "reviewed"

/**
 * La durée de vie d'une carte au-delà de son signal (point 12) : la durée de vie est le signal, sauf
 * l'activité choisie seul (10 min) et le rendu (tant qu'il attend validation). Une activité dont on ne
 * connaît pas le statut attend toujours : la prod la gardait jusqu'au geste.
 */
export function signalEnd(
    signal: Pick<TutorLiveSignal, "type">,
    startedAt: number,
    now: number,
    reviewStatus?: string | null
): TutorSignalEnd | null {
    if (signal.type === "auto_activity" && now - startedAt >= AUTO_ACTIVITY_LIFETIME_MS) return "expired"
    if (signal.type === "exercise_done" && (reviewStatus === "validated" || reviewStatus === "rejected"))
        return "reviewed"
    return null
}

/** Le contexte du champion que « Envoyer » demande (`checkpointTypeForEvent`). */
export function checkpointContextOf(type: TutorLiveSignalType): "raise_hand" | "silent_mode_10min" | null {
    if (type === "hand_raised") return "raise_hand"
    if (type === "silence") return "silent_mode_10min"
    return null
}

// ── Le collage et le dépôt d'un support (web) ────────────────────────────────────────────────────

/** Les types qu'accepte la zone de dépôt (`ImageUploadZone.ACCEPTED_TYPES`). */
export const ACCEPTED_DROP_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "image/webp",
    "application/pdf",
]

type ClipboardLike = { items?: ArrayLike<{ type: string; getAsFile: () => File | null }> | null } | null | undefined

/** La première image du presse-papiers (`ImageUploadZone.handlePaste`) : les images seules, comme le legacy. */
export function pastedImage(clipboard: ClipboardLike): File | null {
    const items = clipboard?.items
    if (items == null) return null
    for (let i = 0; i < items.length; i++) {
        const item = items[i]
        if (item.type.startsWith("image/")) return item.getAsFile()
    }
    return null
}

/** Le premier fichier déposé que la zone accepte (`useDropzone` sur `ACCEPTED_TYPES`). */
export function droppedFile(files: ArrayLike<File> | null | undefined): File | null {
    if (files == null) return null
    for (let i = 0; i < files.length; i++) {
        if (ACCEPTED_DROP_TYPES.includes(files[i].type)) return files[i]
    }
    return null
}
